import { t, currentLang } from "@/i18n";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { GraduationCap, Heart, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Field = ({ label, testid, ...props }) => (
  <label className="block">
    <span className="text-sm font-bold text-slate-700">{label}</span>
    <input data-testid={testid} {...props}
      className="mt-1.5 w-full rounded-2xl border-2 border-slate-200 bg-white px-4 py-3.5 text-base outline-none focus:border-[#FF5252] transition-colors" />
  </label>
);

export default function AuthPage({ mode }) {
  const isSignup = mode === "signup";
  const { setUser } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "parent" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const body = isSignup ? { ...form, language: currentLang() } : { email: form.email, password: form.password };
      const { data } = await api.post(isSignup ? "/auth/register" : "/auth/login", body);
      setUser(data);
      toast.success(isSignup ? `Welcome, ${data.name}! You have ${data.credits} free credits.` : `Welcome back, ${data.name}!`);
      nav(loc.state?.from || "/create", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] dot-grid flex items-center justify-center px-4 py-12">
      <form onSubmit={submit} className="card-soft w-full max-w-md p-8 sm:p-10 space-y-5 rise" data-testid={`${mode}-form`}>
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">{isSignup ? "Make an account" : "Welcome back"}</h1>
          <p className="text-slate-500 mt-2">{isSignup ? "5 free colouring pages to start. No card needed." : "Log in to keep creating."}</p>
        </div>
        {isSignup && (
          <>
            <Field label={t("your_name")} testid="signup-name-input" value={form.name} onChange={set("name")} required maxLength={60} />
            <div>
              <span className="text-sm font-bold text-slate-700">I'm a...</span>
              <div className="grid grid-cols-2 gap-3 mt-1.5">
                {[{ id: "parent", label: t("parent"), icon: Heart }, { id: "teacher", label: t("teacher"), icon: GraduationCap }].map((r) => (
                  <button type="button" key={r.id} data-testid={`signup-role-${r.id}`} onClick={() => setForm((f) => ({ ...f, role: r.id }))}
                    className={`flex items-center justify-center gap-2 py-3.5 rounded-2xl border-2 font-bold transition-colors ${form.role === r.id ? "border-[#1E293B] bg-[#FFD166]" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                    <r.icon className="w-4 h-4" /> {r.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
        <Field label={t("email")} type="email" testid={`${mode}-email-input`} value={form.email} onChange={set("email")} required />
        <Field label={t("password")} type="password" testid={`${mode}-password-input`} value={form.password} onChange={set("password")} required minLength={isSignup ? 6 : 1} />
        {error && <p className="text-sm font-bold text-red-600 bg-red-50 rounded-xl px-4 py-3" data-testid={`${mode}-error`}>{error}</p>}
        <button type="submit" disabled={busy} className="btn-pop btn-red w-full text-lg" data-testid={`${mode}-submit-button`}>
          {busy && <Loader2 className="w-5 h-5 animate-spin" />} {isSignup ? "Create account" : "Log in"}
        </button>
        <p className="text-center text-sm text-slate-600">
          {isSignup ? "Already have an account? " : "New here? "}
          <Link to={isSignup ? "/login" : "/signup"} className="font-bold text-[#FF5252] hover:underline" data-testid={`${mode}-switch-link`}>
            {isSignup ? "Log in" : "Create an account"}
          </Link>
        </p>
      </form>
    </div>
  );
}
